import EngineCleanupBase from '../../../../services/engines/EngineCleanupBase';
import ComponentNotifier from '../../../../services/wss/ComponentNotifier';
import { subscribe } from '../../../../services/utils/pubsub';
import { events } from '../../../../services/wss/SystemEvents';
import { EngineNames } from '../../../../services/wss/events/EngineNames';
import userStateEngine2 from '../../../../controllers/UserState/UserStateEngine2';
import { UserStateKeys } from '../../../../controllers/UserState/UserStateKeys';
import userEngine from '../../../../controllers/User/UserEngine';
import searchEngine from './SearchEngine';

const _name = EngineNames.SearchQueryEngine;

class SearchQueryEngine extends EngineCleanupBase {
    _query = '';
    _onUserLoaded = null;

    bootup = () => {
        this.initialize(_name);

        // Restore query from userState after user is loaded
        this._onUserLoaded = subscribe(events.onUserLoaded, () => this._onRestored());

        // If user already loaded before bootup — restore immediately (slow devices)
        if (userEngine.getUser()) this._onRestored();
    };

    /**
     * Sets the query, persists it to userState, notifies UI, and triggers search if length >= 3.
     * Auto-selects first result after search — tab may not be mounted yet when this fires.
     * CHANGED — persist to userState here. Previously setQuery never wrote searchQuery back,
     * so _onRestored kept re-applying whatever stale value was last saved (or never saved at
     * all), overwriting the user's current query whenever onUserLoaded re-fired (e.g. on tab
     * switch triggering a user refresh elsewhere).
     * @param {string} query
     */
    setQuery = async (query, doExecute = true) => {
        this._query = query;
        this._notify();
        userStateEngine2.set([{ name: UserStateKeys.searchQuery, value: query }]);

        if (query.length >= 3 && doExecute) {
            await searchEngine.startSearch(query);
            // Auto-select first result — tab initializes from cache so it picks this up on mount
            const results = searchEngine.getResults();
            if (results.length > 0) searchEngine.selectEntry(results[0]);
        } else {
            searchEngine.clear();
        }
    };

    /**
     * Clears query and results — triggered by Escape key.
     * CHANGED — also persist the cleared (empty) query, otherwise a stale non-empty
     * value stays in userState and reappears on the next _onRestored() call.
     */
    clearQuery = () => {
        this._query = '';
        this._notify();
        userStateEngine2.set([{ name: UserStateKeys.searchQuery, value: '' }]);
        searchEngine.clear();
    };

    /** @returns {string} */
    getQuery = () => this._query;

    clear = () => {
        this._query = '';
        super.clear();
    };

    // ─── private ──────────────────────────────────────────

    /**
     * Restores query from userState after user is loaded.
     * If no saved query — user has no luck.
     */
    _onRestored = () => {
        const saved = userStateEngine2.getValue(UserStateKeys.searchQuery);
        if (!saved || saved.length < 3) return;

        this.setQuery(saved, false);
    };

    _notify = () => {
        ComponentNotifier.notify(_name, 'queryChanged', { query: this._query });
    };
}

const searchQueryEngine = new SearchQueryEngine();
export default searchQueryEngine;
